// @mui material components
import {
  Snackbar,
  Alert,
  Grid,
  Card,
  CircularProgress,
  Backdrop,
  Tooltip,
  Icon,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";

// Material Dashboard 2 React components
import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import MDButton from "theme/components/MDButton";

// Material Dashboard 2 React example components
import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";

// import { useAuthUser, useAuthHeader } from "react-auth-kit";

import { useEffect, useState } from "react";
import axios from "axios.js";
import { useIsAuthenticated } from "react-auth-kit";
import { useNavigate } from "react-router-dom";

import titleize from "titleize";
import usePageTitle from "app/pageTitle";

function Beneficiaries() {
  const isAuthenticated = useIsAuthenticated();
  const navigate = useNavigate();
  if (!isAuthenticated()) {
    navigate("/sign-in");
  }

  const pageTitle = "My Beneficiaries";
  usePageTitle(pageTitle);

  const [refreshPage, setRefreshPage] = useState(false);
  const [beneficiaries, setBeneficiaries] = useState([]);

  const [showLoading, setShowLoading] = useState(false);
  const [severity, setSeverity] = useState("error");
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };

  useEffect(() => {
    const fetchData = async () => {
      setShowLoading(true);
      await axios
        .get("users/beneficiaries.json")
        .then((result) => {
          // console.log(result);
          setShowLoading(false);
          if (result.data.status === "success") {
            setBeneficiaries(result.data.data.beneficiaries);
          } else {
            setSeverity("error");
            setErrMsg(result.data.data.message);
            setOpen(true);
          }
        })
        .catch(() => {
          setShowLoading(false);
          setSeverity("error");
          setErrMsg("An error occurred. Make sure you have an active internet connection.");
          setOpen(true);
        });
    };

    fetchData();
  }, [refreshPage]);

  const [openCancelDialog, setCancelDialog] = useState(false);
  const [currentBeneficiaryReference, setCurrentBeneficiaryReference] = useState(null);

  async function deleteBeneficiary() {
    setShowLoading(true);
    const json = { reference: `${currentBeneficiaryReference}` };
    await axios
      .post("users/beneficiaries/delete.json", json)
      .then((result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);

          setRefreshPage(!refreshPage);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg("An error occurred. Make sure you have an active internet connection.");
        setOpen(true);
      });
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
        onClick={handleClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>
      <Dialog open={openCancelDialog}>
        <DialogTitle>
          <MDBox>Delete Beneficiary?</MDBox>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            <MDBox>Are you sure you want to delete this beneficiary?</MDBox>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <MDButton
            onClick={() => {
              setCancelDialog(false);
            }}
            color="dark"
            variant="outlined"
          >
            Cancel
          </MDButton>
          <MDButton
            color="error"
            variant="outlined"
            onClick={() => {
              setCancelDialog(false);
              deleteBeneficiary();
            }}
          >
            Delete
          </MDButton>
        </DialogActions>
      </Dialog>
      <MDBox pt={3} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={12} lg={12}>
                <Card sx={{ width: "100%", marginTop: "-5px" }}>
                  <MDBox
                    pt={2}
                    px={2}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <MDTypography variant="h6" fontWeight="medium">
                      My Beneficiaries
                    </MDTypography>
                    <MDButton
                      variant="contained"
                      color="dark"
                      onClick={() => {
                        navigate("/beneficiaries/create");
                      }}
                    >
                      <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                      &nbsp; new Beneficiary
                    </MDButton>
                  </MDBox>
                  {beneficiaries <= 0 ? (
                    <MDBox pt={3} pb={2} px={2}>
                      <MDBox
                        component="ul"
                        display="flex"
                        flexDirection="column"
                        p={0}
                        m={0}
                        sx={{ listStyle: "none" }}
                      >
                        <small>No beneficiaries found.</small>
                      </MDBox>
                    </MDBox>
                  ) : (
                    <MDBox pt={3} pb={2} px={0}>
                      <MDBox display="flex" flexDirection="column" p={0} m={0}>
                        {beneficiaries.map((record, index) => (
                          <MDBox
                            idx={index}
                            borderRadius="lg"
                            p={3}
                            sx={{
                              marginLeft: "10px",
                              marginTop: "5px",
                              marginRight: "10px",
                              marginBottom: "15px",
                              border: ({ borders: { borderWidth, borderColor } }) =>
                                `${borderWidth[1]} solid ${borderColor}`,
                            }}
                          >
                            <MDBox mb={2} lineHeight={0}>
                              <MDBox mb={5} mt={0} lineHeight={0}>
                                <Grid container spacing={1}>
                                  <Grid item xs={6} lg={6} md={6}>
                                    <MDTypography
                                      variant="h6"
                                      fontWeight="medium"
                                      textTransform="capitalize"
                                    >
                                      Beneficiary
                                    </MDTypography>
                                    <MDTypography
                                      variant="caption"
                                      fontWeight="light"
                                      textTransform="capitalize"
                                    >
                                      {record.alias}
                                    </MDTypography>
                                  </Grid>
                                  <Grid item xs={6} lg={6} md={6}>
                                    <MDBox
                                      display="flex"
                                      justifyContent="flex-end"
                                      alignItems="center"
                                    >
                                      <Tooltip title="Delete Beneficiary" placement="top">
                                        <MDButton
                                          variant="outlined"
                                          color="error"
                                          iconOnly
                                          circular
                                          onClick={() => {
                                            setCurrentBeneficiaryReference(record.reference);
                                            setCancelDialog(true);
                                          }}
                                        >
                                          <Icon
                                            sx={{ cursor: "pointer" }}
                                            fontSize="small"
                                            color="dark"
                                          >
                                            delete
                                          </Icon>
                                        </MDButton>
                                      </Tooltip>
                                    </MDBox>
                                  </Grid>
                                </Grid>
                              </MDBox>
                              <Grid container spacing={1} xs={12} lg={12} md={12}>
                                <Grid item xs={4} lg={4} md={4}>
                                  <MDBox mb={0.5} lineHeight={0}>
                                    <MDTypography variant="caption" color="text">
                                      Currency:
                                    </MDTypography>
                                  </MDBox>
                                  <MDBox mb={1} lineHeight={0}>
                                    <MDTypography
                                      variant="caption"
                                      fontWeight="medium"
                                      textTransform="capitalize"
                                    >
                                      {record.currency}
                                    </MDTypography>
                                  </MDBox>
                                </Grid>
                                <Grid item xs={4} lg={4} md={4}>
                                  <MDBox mb={0.5} lineHeight={0}>
                                    <MDTypography variant="caption" color="text">
                                      Country:
                                    </MDTypography>
                                  </MDBox>
                                  <MDBox mb={1} lineHeight={0}>
                                    <MDTypography
                                      variant="caption"
                                      fontWeight="medium"
                                      textTransform="capitalize"
                                    >
                                      {record.country}
                                    </MDTypography>
                                  </MDBox>
                                </Grid>
                                <Grid item xs={4} lg={4} md={4}>
                                  <MDBox mb={0.5} lineHeight={0}>
                                    <MDTypography variant="caption" color="text">
                                      Beneficiary Type:
                                    </MDTypography>
                                  </MDBox>
                                  <MDBox mb={1} lineHeight={0}>
                                    <MDTypography
                                      variant="caption"
                                      fontWeight="medium"
                                      textTransform="capitalize"
                                    >
                                      {record.beneficiary_type}
                                    </MDTypography>
                                  </MDBox>
                                </Grid>
                                <Grid item xs={4} lg={4} md={4}>
                                  <MDBox mb={0.5} lineHeight={0}>
                                    <MDTypography variant="caption" color="text">
                                      Relationship Type:
                                    </MDTypography>
                                  </MDBox>
                                  <MDBox mb={1} lineHeight={0}>
                                    <MDTypography
                                      variant="caption"
                                      fontWeight="medium"
                                      textTransform="capitalize"
                                    >
                                      {record.beneficiary_relationship_type}
                                    </MDTypography>
                                  </MDBox>
                                </Grid>
                                {record.beneficiary_type === "individual" ? (
                                  <>
                                    <Grid item xs={4} lg={4} md={4}>
                                      <MDBox mb={0.5} lineHeight={0}>
                                        <MDTypography variant="caption" color="text">
                                          Last Name:
                                        </MDTypography>
                                      </MDBox>
                                      <MDBox mb={1} lineHeight={0}>
                                        <MDTypography
                                          variant="caption"
                                          fontWeight="medium"
                                          textTransform="capitalize"
                                        >
                                          {record.last_name}
                                        </MDTypography>
                                      </MDBox>
                                    </Grid>
                                    <Grid item xs={4} lg={4} md={4}>
                                      <MDBox mb={0.5} lineHeight={0}>
                                        <MDTypography variant="caption" color="text">
                                          Other Names:
                                        </MDTypography>
                                      </MDBox>
                                      <MDBox mb={1} lineHeight={0}>
                                        <MDTypography
                                          variant="caption"
                                          fontWeight="medium"
                                          textTransform="capitalize"
                                        >
                                          {record.first_name}
                                        </MDTypography>
                                      </MDBox>
                                    </Grid>
                                  </>
                                ) : (
                                  <Grid item xs={4} lg={4} md={4}>
                                    <MDBox mb={0.5} lineHeight={0}>
                                      <MDTypography variant="caption" color="text">
                                        Company Name:
                                      </MDTypography>
                                    </MDBox>
                                    <MDBox mb={1} lineHeight={0}>
                                      <MDTypography
                                        variant="caption"
                                        fontWeight="medium"
                                        textTransform="capitalize"
                                      >
                                        {record.company_name}
                                      </MDTypography>
                                    </MDBox>
                                  </Grid>
                                )}
                                <Grid item xs={4} lg={4} md={4}>
                                  <MDBox mb={0.5} lineHeight={0}>
                                    <MDTypography variant="caption" color="text">
                                      Address:
                                    </MDTypography>
                                  </MDBox>
                                  <MDBox mb={1} lineHeight={0}>
                                    <MDTypography
                                      variant="caption"
                                      fontWeight="medium"
                                      textTransform="capitalize"
                                    >
                                      {record.address}
                                    </MDTypography>
                                  </MDBox>
                                </Grid>
                                <Grid item xs={4} lg={4} md={4}>
                                  <MDBox mb={0.5} lineHeight={0}>
                                    <MDTypography variant="caption" color="text">
                                      City:
                                    </MDTypography>
                                  </MDBox>
                                  <MDBox mb={1} lineHeight={0}>
                                    <MDTypography
                                      variant="caption"
                                      fontWeight="medium"
                                      textTransform="capitalize"
                                    >
                                      {record.city}
                                    </MDTypography>
                                  </MDBox>
                                </Grid>
                                <Grid item xs={4} lg={4} md={4}>
                                  <MDBox mb={0.5} lineHeight={0}>
                                    <MDTypography variant="caption" color="text">
                                      State:
                                    </MDTypography>
                                  </MDBox>
                                  <MDBox mb={1} lineHeight={0}>
                                    <MDTypography
                                      variant="caption"
                                      fontWeight="medium"
                                      textTransform="capitalize"
                                    >
                                      {record.state}
                                    </MDTypography>
                                  </MDBox>
                                </Grid>
                              </Grid>
                            </MDBox>
                            <MDBox mb={2} mt={4} lineHeight={0}>
                              <MDBox mb={2} mt={4} lineHeight={0}>
                                <MDTypography
                                  variant="h6"
                                  fontWeight="medium"
                                  textTransform="capitalize"
                                >
                                  Payment Method
                                </MDTypography>
                              </MDBox>
                              <Grid container spacing={1} xs={12} lg={12} md={12}>
                                {Object.keys(record.banking_details).map((key) => (
                                  <Grid item xs={6} lg={6} md={6}>
                                    <MDBox mb={0.5} lineHeight={0}>
                                      <MDTypography variant="caption" color="text">
                                        {titleize(key).replace("_", " ")}:&nbsp;&nbsp;&nbsp;
                                      </MDTypography>
                                    </MDBox>
                                    <MDBox mb={1} lineHeight={0}>
                                      <MDTypography
                                        variant="caption"
                                        fontWeight="medium"
                                        textTransform="capitalize"
                                      >
                                        {record.banking_details[key]}
                                      </MDTypography>
                                    </MDBox>
                                  </Grid>
                                ))}
                              </Grid>
                            </MDBox>
                          </MDBox>
                        ))}
                      </MDBox>
                    </MDBox>
                  )}
                </Card>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Beneficiaries;
